import { AdvancedSelect } from '@orobix/orobix-fe-components'
import { useField, useFormikContext } from 'formik'
import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { USER_LEVEL } from '../../../components/Settings/definitions'
import { SettingsContext } from '../../../components/Settings/SettingsContext'
import { ProductType } from '../../../types'
import { BackboneSelectOption } from '../../../types/backbone'
import { SelectOption } from './types'
import { useResolutionData } from './useResolutionData'
import { resolutionToOption } from './utils'

export interface ResolutionSelectProps {
  fieldName: string
  productId: number
  disabled: boolean
  dftValue: SelectOption | undefined
  onSelectedResolution: React.Dispatch<
    React.SetStateAction<SelectOption | undefined>
  >
  setCustomHeight: React.Dispatch<React.SetStateAction<number>>
  setCustomWidth: React.Dispatch<React.SetStateAction<number>>
  backboneValues: BackboneSelectOption[] | undefined
  producType: ProductType
}

const ResolutionSelect = ({
  fieldName,
  productId,
  disabled,
  dftValue,
  onSelectedResolution,
  setCustomHeight,
  setCustomWidth,
  backboneValues,
  producType,
}: ResolutionSelectProps) => {
  const { t } = useTranslation()
  const { isSubmitting } = useFormikContext()
  const [field, meta, helpers] = useField<SelectOption | undefined>(fieldName)

  const { name, value } = field
  const { error, touched } = meta
  const { setValue, setTouched } = helpers
  const { resolutions, isLoading } = useResolutionData(productId)
  const toOption = resolutionToOption()
  const DFT_HEIGHT = 224
  const DFT_WIDTH = 224

  const { settings } = useContext(SettingsContext)

  function resetToStandardValues() {
    setValue(dftValue)
    setCustomHeight(DFT_HEIGHT)
    setCustomWidth(DFT_WIDTH)
  }

  useEffect(() => {
    if (backboneValues === undefined || backboneValues.length === 0) {
      resetToStandardValues()
    } else if (producType === 'segmentation') {
      const backboneStandardFound = backboneValues.filter(
        (obj) => obj.label.toLowerCase() === 'standard'
      )
      if (backboneStandardFound) {
        resetToStandardValues()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backboneValues])

  const backboneWithResolutionNotSupported = backboneValues
    ? backboneValues.filter(
        (b) => b.metadata.trainingResolutionSupported === false
      )
    : []

  const getHelpMessage = (): string => {
    if (settings.preference__user_level === USER_LEVEL.basic) {
      return t(
        'Resolution is enabled only for expert users. The user level can be changed in the settings.'
      )
    }
    if (
      !disabled &&
      backboneValues &&
      backboneValues.length >= 1 &&
      backboneWithResolutionNotSupported.length > 0 &&
      backboneValues.length > backboneWithResolutionNotSupported.length
    ) {
      return `${
        backboneWithResolutionNotSupported.length === 1
          ? t(
              'The selected resolution is not applied to the training with the backbone'
            )
          : t(
              'The selected resolution is not applied to the trainings with the backbones'
            )
      }: ${backboneWithResolutionNotSupported
        .map((b) => b.label.split(' [')[0])
        .join(', ')}.`
    }
    return ''
  }

  return (
    <div id="divResolutionId">
      <AdvancedSelect
        name={name}
        id={name}
        label={t('Resolution')}
        value={value}
        onChange={(v: SelectOption | null) => {
          if (v == null) {
            const standardValue = { value: 'standard', label: 'Standard' }
            setValue(standardValue)
            onSelectedResolution(standardValue)
            setCustomHeight(DFT_HEIGHT)
            setCustomWidth(DFT_WIDTH)
          } else {
            setValue(v || undefined)
            onSelectedResolution(v)
            if (v.value === 'standard') {
              setCustomHeight(DFT_HEIGHT)
              setCustomWidth(DFT_WIDTH)
            }
            if (v.value === 'high') {
              setCustomHeight(448)
              setCustomWidth(448)
            }
            if (v.value === 'custom') {
              setCustomHeight(DFT_HEIGHT)
              setCustomWidth(DFT_WIDTH)
            }
          }
        }}
        onBlur={() => setTouched(true)}
        options={resolutions ? resolutions.map(toOption) : []}
        error={!!(error && touched)}
        errorMessage={error}
        isDisabled={
          isLoading ||
          isSubmitting ||
          disabled ||
          (backboneValues &&
            backboneValues.length > 0 &&
            backboneValues.length === backboneWithResolutionNotSupported.length)
        }
        noOptionsMessage={() => t('No resolution found')}
        placeholder={resolutions ? t('Select...') : t('Loading options...')}
        defaultValue={dftValue}
        helpMessage={getHelpMessage()}
      />
    </div>
  )
}

export default ResolutionSelect
